import React, { PropsWithChildren } from 'react';
import { ModuleTopBar, Box, Flex, Grid } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import FlowProductsBottomBar from '@flows/src/components/flow-products/FlowProductsBottomBar';
import FlowProductsAnvaSidebar from '@flows/src/components/flow-products/FlowProductsAnvaSidebar';
import { BasketCalculationProvider } from '@flows/src/components/flow-products/quotes/basket/BasketCalculationProvider';
import { useHistory, useParams } from 'react-router';
import useQueryParams from '@common/src/hooks/useQueryParams';
import { useFlowsSession } from '@flows/src/api/getFlowsSessionById';
import useStartAnvaFlow from '@sales-conversation/src/hooks/anva/useStartAnvaFlow';
import { useQueryClient } from 'react-query';

const FlowProductsLayout: React.FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const sessionId = queryParams.get('session_id') || undefined;
  const { data: session } = useFlowsSession(sessionId);
  const startFlow = useStartAnvaFlow();
  const history = useHistory();
  const { flowId } = useParams<{ flowId: string }>();
  const queryClient = useQueryClient();

  const getBackText = () => {
    if (window.location.pathname.includes('selection-overview')) {
      return t('BACK-TO-DASHBOARD');
    }
    if (window.location.pathname.includes('basket')) {
      return t('BACK-TO-OVERVIEW');
    }
    return t('Back to questions');
  };
  const handlePrevious = () => {
    if (window.location.pathname.includes('basket')) {
      // Go back to the previous page (most likely the overview page with the
      // correct tagId preserved in the URL) instead of pushing a new URL that
      // might lose the tagId. This prevents navigation to `/overview` without
      // a tag and ensures the user returns to the exact overview they came
      // from.
      return history.goBack();
    }
    if (flowId && sessionId) {
      // Clear the flow-products cache when navigating back to questionnaire
      // This ensures that when the user returns to the flow-products overview,
      // new quotes are calculated and the redirect logic works properly
      queryClient.removeQueries(['flow-products', 'anva', 'quotes']);
      queryClient.removeQueries(['flow-products', 'anva', 'completed-quotes']);
      queryClient.removeQueries(['flow-products', 'anva', 'quotes-v2']);

      // history.push(`/flows/${flowId}/questions/${sessionId}`);
      startFlow({
        flowId: flowId,
        uuid: sessionId,
        partyId: session?.prospect?.id,
        anva_products: session?.anva_products || [],
      });
    }
  };

  const getTopBarText = (): string => {
    if (window.location.pathname.includes('selection-overview')) {
      return t('FLOWS_PRODUCTS_SELECTION-OVERVIEW_TOP-BAR_TITLE');
    }
    return t('CONVERSATIONS_REPORT-CREATION_PRODUCT-SELECTION');
  };

  const checkIsBasketPage = () => {
    const path = window.location.pathname;
    return path.includes('basket') || path.includes('selection-overview');
  };
  const isBasketPage = checkIsBasketPage();

  // The core layout content
  const layoutContent = (
    <Grid
      templateColumns={{ base: 'auto minmax(0, 1fr)' }}
      w="100%"
      h="100%"
      position="relative"
      bg="red"
      minH="0"
    >
      <FlowProductsAnvaSidebar />
      <Grid templateRows="auto 1fr auto" position="relative" w="100%" minW="0">
        <Box position="sticky" top="0" bg="white" zIndex="1">
          <ModuleTopBar title={getTopBarText()} />
        </Box>
        <Flex
          flexDirection="column"
          align="center"
          flexGrow="1"
          bg="gray.ultraLight"
          overflow="auto"
          minW="0"
        >
          {children}
        </Flex>
        <FlowProductsBottomBar
          handlePrevious={handlePrevious}
          backText={getBackText()}
        />
      </Grid>
    </Grid>
  );

  // Wrap with BasketCalculationProvider when on basket page so that calculation
  // state is shared between the page content and the bottom bar actions.
  if (isBasketPage) {
    return (
      <BasketCalculationProvider sessionId={sessionId || ''}>
        {layoutContent}
      </BasketCalculationProvider>
    );
  }

  return layoutContent;
};

export default FlowProductsLayout;
